import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Page, Button, Spinner, FullBox, TableList, Typography, DropdownMenu, TableListActions, useOverlayContext } from 'seagull';
import { Edit, Refresh, Trash } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import ExternalLinkButton from '~/components/ExternalLinkButton';
import RestfulWrapper from '~/components/RestfulWrapper/RestfulWrapper';
import { useStoreActions } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { formatShortMonthUSDate } from '~/utils';
import { APIKeysEmptyState } from './APIKeysEmptyState';
import { APIKeysSortField, apiKeysColumnDefs, apiKeysHeaderItems, formatAPIKeyValue } from './APIKeysPage.utils';
import { DeactivateAPIKeyModal } from './modals/DeactivateAPIKeyModal';
import GenerateAPIKeyModal from './modals/GenerateAPIKeyModal';
import { RegenerateAPIKeyModal } from './modals/RegenerateAPIKeyModal';
import { RenameAPIKeyModal } from './modals/RenameAPIKeyModal';
export const GetAPIKeysPageTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            APIKeysPageTID: {
                DocsLink: 'anchor-api-docs-link',
                Page: 'api-keys-page',
                Error: 'api-keys-error',
                GenerateButton: 'generate-api-key-button',
                TableHeader: 'api-keys-table-header',
                Name: (index)=>"api-key-name-".concat(index),
                Id: (index)=>"api-key-id-".concat(index),
                Created: (index)=>"api-key-created-".concat(index),
                Updated: (index)=>"api-key-updated-".concat(index),
                Actions: (index)=>"api-key-actions-".concat(index),
                Regenerate: (index)=>"regenerate-api-key-".concat(index),
                Rename: (index)=>"rename-api-key-".concat(index),
                Deactivate: (index)=>"deactivate-api-key-".concat(index)
            }
        }
    });
const normalizeName = (name)=>name.trim().toLowerCase();
const APIKeysSubheader = ()=>{
    const { t: apiKeysPageTexts } = useLocaleKeys('pages.apiKeysPage');
    const { APIKeysPageTID } = useMemo(()=>GetAPIKeysPageTID(), []);
    return /*#__PURE__*/ _jsxs(Typography, {
        children: [
            apiKeysPageTexts.accessSubheader(),
            ' ',
            /*#__PURE__*/ _jsx(ExternalLinkButton, {
                href: "https://docs.sayanchor.com",
                underline: "none",
                openInNewTab: true,
                testId: APIKeysPageTID.DocsLink(),
                bi: {
                    name: 'anchor_api_docs_link'
                },
                children: apiKeysPageTexts.anchorApi()
            })
        ]
    });
};
export function getIsNameValid(name, keys, currentName) {
    const trimmedName = normalizeName(name);
    if (currentName && trimmedName === normalizeName(currentName)) {
        return true;
    }
    return !keys.find((key)=>normalizeName(key.name) === trimmedName);
}
const APIKeysPage = ()=>{
    const { t: apiKeysPageTexts } = useLocaleKeys('pages.apiKeysPage');
    const overlayContext = useOverlayContext();
    const { api } = useStoreActions();
    const { APIKeysPageTID } = useMemo(()=>GetAPIKeysPageTID(), []);
    const [sort, setSort] = useState({
        field: APIKeysSortField.Name,
        direction: 'asc'
    });
    const handleGenerateAPIKey = (keys, refresh)=>{
        overlayContext.showModal({
            biName: 'generate_api_key_modal',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(GenerateAPIKeyModal, {
                    getIsNameValid: (name)=>getIsNameValid(name, keys),
                    dismissModal: dismissModal,
                    refresh: refresh
                });
            },
            closeOnClickOutside: true,
            closeOnEscapeKey: true
        });
    };
    const fetchAPIKeys = ()=>api.business.getAPIKeys({
            sortBy: sort.field,
            sortDirection: sort.direction
        });
    const handleSortChange = (tableSort)=>{
        if (!tableSort) {
            setSort({
                field: APIKeysSortField.Name,
                direction: 'asc'
            });
        } else {
            const fieldMap = {
                0: APIKeysSortField.Name,
                2: APIKeysSortField.CreatedOn,
                3: APIKeysSortField.UpdatedOn
            };
            setSort({
                field: fieldMap[tableSort.colIndex],
                direction: tableSort.sort
            });
        }
    };
    return /*#__PURE__*/ _jsx(RestfulWrapper, {
        fetch: fetchAPIKeys,
        renderLoader: ()=>/*#__PURE__*/ _jsx(Page, {
                header: apiKeysPageTexts.header(),
                subheader: /*#__PURE__*/ _jsx(APIKeysSubheader, {}),
                testId: APIKeysPageTID.Page(),
                children: /*#__PURE__*/ _jsx(Spinner, {
                    size: 24
                })
            }),
        renderError: ()=>/*#__PURE__*/ _jsx(Page, {
                header: apiKeysPageTexts.header(),
                subheader: /*#__PURE__*/ _jsx(APIKeysSubheader, {}),
                testId: APIKeysPageTID.Page(),
                children: /*#__PURE__*/ _jsx(FullBox, {
                    testId: APIKeysPageTID.Error(),
                    children: /*#__PURE__*/ _jsx(Typography, {
                        children: apiKeysPageTexts.errorLoading()
                    })
                })
            }),
        renderChild: (param)=>{
            let { data: { keys }, refresh } = param;
            return /*#__PURE__*/ _jsx(Page, {
                header: apiKeysPageTexts.header(),
                variant: "containedHeight",
                subheader: /*#__PURE__*/ _jsx(APIKeysSubheader, {}),
                actions: keys.length > 0 ? /*#__PURE__*/ _jsx(Button, {
                    bi: {
                        name: 'generate_api_key'
                    },
                    testId: APIKeysPageTID.GenerateButton(),
                    onClick: ()=>handleGenerateAPIKey(keys, refresh),
                    children: apiKeysPageTexts.generateApiKey()
                }) : null,
                testId: APIKeysPageTID.Page(),
                children: keys.length > 0 ? /*#__PURE__*/ _jsx(TableList.Provider, {
                    columnDefs: apiKeysColumnDefs,
                    hasActionColumn: true,
                    sort: {
                        sort: sort.direction,
                        colIndex: apiKeysHeaderItems.findIndex((item)=>item.id === sort.field)
                    },
                    onSortChange: handleSortChange,
                    children: /*#__PURE__*/ _jsx(TableList.Structure, {
                        header: /*#__PURE__*/ _jsx(TableList.Header, {
                            items: apiKeysHeaderItems,
                            testId: APIKeysPageTID.TableHeader()
                        }),
                        content: keys.map((key, index)=>/*#__PURE__*/ _jsx(TableList.Row, {
                                items: [
                                    {
                                        value: key.name,
                                        testId: APIKeysPageTID.Name(index)
                                    },
                                    {
                                        value: formatAPIKeyValue(key.id),
                                        testId: APIKeysPageTID.Id(index)
                                    },
                                    {
                                        value: formatShortMonthUSDate(new Date(key.createdOn)),
                                        testId: APIKeysPageTID.Created(index)
                                    },
                                    {
                                        value: formatShortMonthUSDate(new Date(key.updatedOn)),
                                        testId: APIKeysPageTID.Updated(index)
                                    }
                                ],
                                actionColumn: /*#__PURE__*/ _jsxs(TableListActions, {
                                    bi: {
                                        button: {
                                            name: 'api_key_view'
                                        },
                                        menuTrigger: {
                                            name: 'api_key_actions'
                                        }
                                    },
                                    testId: APIKeysPageTID.Actions(index),
                                    children: [
                                        /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                                            bi: {
                                                name: 'regenerate_api_key'
                                            },
                                            iconStart: /*#__PURE__*/ _jsx(Refresh, {}),
                                            onClick: ()=>{
                                                overlayContext.showModal({
                                                    biName: 'regenerate_api_key_modal',
                                                    content: (param)=>{
                                                        let { dismissModal } = param;
                                                        return /*#__PURE__*/ _jsx(RegenerateAPIKeyModal, {
                                                            apiKey: keys[index],
                                                            dismissModal: dismissModal,
                                                            refresh: refresh
                                                        });
                                                    },
                                                    closeOnClickOutside: true,
                                                    closeOnEscapeKey: true
                                                });
                                            },
                                            testId: APIKeysPageTID.Regenerate(index),
                                            children: apiKeysPageTexts.regenerateApiKeyAction()
                                        }),
                                        /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                                            bi: {
                                                name: 'rename_api_key'
                                            },
                                            iconStart: /*#__PURE__*/ _jsx(Edit, {}),
                                            onClick: ()=>{
                                                overlayContext.showModal({
                                                    biName: 'rename_api_key_modal',
                                                    content: (param)=>{
                                                        let { dismissModal } = param;
                                                        return /*#__PURE__*/ _jsx(RenameAPIKeyModal, {
                                                            apiKey: keys[index],
                                                            getIsNameValid: (name)=>getIsNameValid(name, keys, keys[index].name),
                                                            dismissModal: dismissModal,
                                                            refresh: refresh
                                                        });
                                                    },
                                                    closeOnClickOutside: true,
                                                    closeOnEscapeKey: true
                                                });
                                            },
                                            testId: APIKeysPageTID.Rename(index),
                                            children: apiKeysPageTexts.renameAction()
                                        }),
                                        /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                                            bi: {
                                                name: 'deactivate_api_key'
                                            },
                                            skin: "error",
                                            iconStart: /*#__PURE__*/ _jsx(Trash, {}),
                                            onClick: ()=>{
                                                overlayContext.showModal({
                                                    biName: 'deactivate_api_key_modal',
                                                    content: (param)=>{
                                                        let { dismissModal } = param;
                                                        return /*#__PURE__*/ _jsx(DeactivateAPIKeyModal, {
                                                            apiKey: keys[index],
                                                            dismissModal: dismissModal,
                                                            refresh: refresh
                                                        });
                                                    },
                                                    closeOnClickOutside: true,
                                                    closeOnEscapeKey: true
                                                });
                                            },
                                            testId: APIKeysPageTID.Deactivate(index),
                                            children: apiKeysPageTexts.deactivateApiKeyAction()
                                        })
                                    ]
                                })
                            }, index))
                    })
                }) : /*#__PURE__*/ _jsx(APIKeysEmptyState, {
                    generateKey: ()=>handleGenerateAPIKey(keys, refresh)
                })
            });
        }
    }, "".concat(sort.field, "-").concat(sort.direction));
};
export default APIKeysPage;
